/**
 * Server-state facade over @tanstack/react-query. Feature code imports
 * useQuery/useMutation/useQueryClient from here; `createQueryClient` holds
 * the app-wide defaults:
 *
 *  - networkMode 'offlineFirst': a query runs once even when the browser
 *    reports offline (navigator.onLine lies on some captive networks), then
 *    pauses retries until connectivity returns. Screens that have an offline
 *    fallback (e.g. My work → offline pack) render it from `isError`.
 *  - retry: network failures and 5xx/408/429 retry up to 3 times with
 *    backoff; 4xx (permission, validation, not found) never retry.
 *  - staleTime 30 s; refetch on reconnect and window focus.
 *  - mutations never retry (callers decide; offline writes go through the
 *    sync outbox, not mutations).
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api/client';

export {
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  if (error instanceof ApiError) return error.isRetryable;
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        networkMode: 'offlineFirst',
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        retry: shouldRetry,
        retryDelay: (n) => Math.min(1000 * 2 ** n, 15_000),
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
      },
      mutations: { networkMode: 'offlineFirst', retry: false },
    },
  });
}

/** Test client: no retries, no background refetching, no garbage-collection timers. */
export function createTestQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, gcTime: Infinity, refetchOnWindowFocus: false, networkMode: 'always' },
      mutations: { retry: false, networkMode: 'always' },
    },
  });
}
